import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { Customer360Accounts } from '../../api/customers.api.js';
import { statementsApi, type AdminStatement } from '../../api/statements.api.js';
import { APP_LOCALE } from '../../utils/locale.js';

function previousMonth(): { start: string; end: string } {
  const today = new Date();
  return {
    start: new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)).toISOString().slice(0, 10),
  };
}
function dateLabel(value: string): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}

export function AccountStatementsDialog({ customerId, customerName, account, onClose }: {
  customerId: string;
  customerName: string;
  account: Customer360Accounts;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [periodStart, setPeriodStart] = useState(() => previousMonth().start);
  const [periodEnd, setPeriodEnd] = useState(() => previousMonth().end);
  const [format, setFormat] = useState<'PDF' | 'CSV'>('PDF');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<{ id: string; message: string } | null>(null);
  const queryKey = ['account-statements', customerId, account.accountId];
  const history = useQuery({
    queryKey,
    queryFn: () => statementsApi.list(customerId, account.accountId),
    retry: false,
  });
  const generate = useMutation({
    mutationFn: () => statementsApi.generate(customerId, account.accountId, { periodStart, periodEnd, format }),
    onSuccess: (statement) => {
      queryClient.setQueryData<AdminStatement[]>(queryKey, (previous) => [statement, ...(previous ?? []).filter((row) => row.id !== statement.id)]);
      void queryClient.invalidateQueries({ queryKey });
    },
  });
  const today = new Date().toISOString().slice(0, 10);
  const periodError = !periodStart || !periodEnd ? 'Choose both dates.'
    : periodStart > periodEnd ? 'The end date must be on or after the start date.'
      : periodEnd > today ? 'The end date cannot be later than today (UTC).' : null;

  const download = async (statement: AdminStatement): Promise<void> => {
    setDownloading(statement.id);
    setDownloadError(null);
    try {
      const blob = await statementsApi.download(customerId, account.accountId, statement.id);
      const url = URL.createObjectURL(blob);
      const extension = statement.format === 'PDF' ? 'pdf' : statement.format === 'CSV' ? 'csv' : statement.format === 'CAMT053_XML' ? 'xml' : 'bin';
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `norvix-statement-${statement.currency}-${statement.periodStart}-${statement.periodEnd}-${statement.id.slice(0, 8)}.${extension}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setDownloadError({ id: statement.id, message: errorMessage(error) });
    } finally { setDownloading(null); }
  };
  const resetResult = (): void => { generate.reset(); setDownloadError(null); };

  return (
    <Dialog open onClose={() => { if (!generate.isPending) onClose(); }} fullWidth maxWidth="sm"
      aria-labelledby="account-statements-title"
      PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: { xs: 'calc(100% - 24px)', sm: 640 }, maxWidth: 640, maxHeight: 'calc(100dvh - 24px)' } }}>
      <DialogTitle id="account-statements-title" sx={{ pr: 7, pb: 1 }}>Account statements</DialogTitle>
      <IconButton aria-label="Close account statements" onClick={onClose} disabled={generate.isPending}
        sx={{ position: 'absolute', top: 12, right: 12, width: 44, height: 44 }}><CloseIcon /></IconButton>
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2, overflowWrap: 'anywhere' }}>
        <Typography variant="body2"><b>{customerName}</b> · {account.currency} account</Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }} title={`Account ${account.accountId}`}>
          {account.iban ? `${account.iban} · Account ${account.accountId.slice(0, 8)}…` : `Account ${account.accountId} · IBAN not assigned`}
        </Typography>
      </Box>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        <Box component="form" onSubmit={(event: React.FormEvent) => { event.preventDefault(); if (!periodError && !generate.isPending) generate.mutate(); }}>
          <Typography variant="subtitle2">Generate a statement</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>Choose the period and file format for this account.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth type="date" label="From" value={periodStart} disabled={generate.isPending}
              InputLabelProps={{ shrink: true }} inputProps={{ max: periodEnd || today }}
              onChange={(event) => { setPeriodStart(event.target.value); resetResult(); }} />
            <TextField fullWidth type="date" label="To" value={periodEnd} disabled={generate.isPending}
              InputLabelProps={{ shrink: true }} inputProps={{ min: periodStart, max: today }}
              onChange={(event) => { setPeriodEnd(event.target.value); resetResult(); }} />
          </Stack>
          {periodError ? <Typography color="error" variant="body2" role="alert" sx={{ mt: 1 }}>{periodError}</Typography> : null}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 2 }}>
            <Typography variant="body2" id="statement-format-label">File format</Typography>
            <ToggleButtonGroup exclusive color="primary" value={format} disabled={generate.isPending} size="small" aria-labelledby="statement-format-label"
              onChange={(_, value: 'PDF' | 'CSV' | null) => { if (value) { setFormat(value); resetResult(); } }}>
              <ToggleButton value="PDF" sx={{ minWidth: 76, minHeight: 44 }}>PDF</ToggleButton>
              <ToggleButton value="CSV" sx={{ minWidth: 76, minHeight: 44 }}>CSV</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 2.5 }}>
            {format === 'PDF' ? 'PDF for reading and sharing.' : 'CSV for spreadsheets.'}
          </Typography>
          {generate.isError ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(generate.error)}</Alert> : null}
          <Button type="submit" variant="contained" disabled={periodError !== null || generate.isPending} sx={{ minHeight: 44 }}
            startIcon={generate.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {generate.isPending ? 'Generating…' : 'Generate statement'}
          </Button>
          <Box aria-live="polite">
            {generate.isPending ? <Typography variant="caption" component="p" sx={{ mt: 1 }}>Preparing your statement…</Typography> : null}
            {generate.data ? <Alert severity="success" sx={{ mt: 2 }}>
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2">Statement ready · {dateLabel(generate.data.periodStart)} – {dateLabel(generate.data.periodEnd)}</Typography>
                <Button size="small" startIcon={<DownloadIcon />} disabled={downloading !== null} onClick={() => { void download(generate.data); }}>
                  {downloading === generate.data.id ? 'Downloading…' : `Download ${generate.data.format}`}
                </Button>
              </Stack>
            </Alert> : null}
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Previous statements</Typography>
          {history.data ? <Typography variant="caption" color="text.secondary">{history.data.length} {history.data.length === 1 ? 'statement' : 'statements'}</Typography> : null}
        </Stack>
        {history.isLoading ? <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={18} /><Typography variant="body2">Loading statements…</Typography></Stack> : null}
        {history.isError ? <Alert severity="error" action={<Button color="inherit" size="small" disabled={history.isFetching} onClick={() => { void history.refetch(); }}>Retry</Button>}>
          Could not load previous statements. {errorMessage(history.error)}
        </Alert> : null}
        {!history.isLoading && !history.isError && history.data?.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No statements have been generated for this account yet.</Typography> : null}
        {downloadError ? <Alert severity="error" sx={{ my: 1 }}>{downloadError.message}</Alert> : null}
        {(history.data ?? []).map((statement) => (
          <Stack key={statement.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between"
            sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{dateLabel(statement.periodStart)} – {dateLabel(statement.periodEnd)}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                <Chip size="small" variant="outlined" label={statement.format === 'CAMT053_XML' ? 'XML' : statement.format} />
                <Typography variant="caption" color="text.secondary">Generated {dateLabel(statement.generatedAt)}</Typography>
              </Stack>
            </Box>
            <Button size="small" startIcon={<DownloadIcon />} disabled={downloading !== null}
              aria-label={`Download ${statement.format} statement ${statement.periodStart} to ${statement.periodEnd}`}
              onClick={() => { void download(statement); }} sx={{ flexShrink: 0, minHeight: 44 }}>
              {downloading === statement.id ? 'Downloading…' : downloadError?.id === statement.id ? 'Retry download' : 'Download'}
            </Button>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}><Button variant="outlined" disabled={generate.isPending} onClick={onClose} sx={{ minHeight: 44 }}>Close</Button></DialogActions>
    </Dialog>
  );
}
